import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import NavBar from '@/components/NavBar';
import Footer from '@/components/Footer';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Search, Plus, Play, Eye, Heart, Calendar } from 'lucide-react';
import { Video, VideoListParams, VideoListResponse, HOLIDAY_OPTIONS, SORT_OPTIONS } from '@/types/video';
import { useAuth } from '@/contexts/AuthContext';
import { formatDuration, generateEmbedUrl } from '@/utils/videoUtils';
import { formatDistanceToNow } from 'date-fns';
import { Link, useSearchParams } from 'react-router-dom';
import VideoLikedBy from '@/components/videos/VideoLikedBy';
import { useVideoLikes } from '@/hooks/useVideoLikes';
import DisplayInfo from '@/components/videos/DisplayInfo';

interface VideoCardProps {
  video: Video;
}

const VideoCard: React.FC<VideoCardProps> = ({ video }) => {
  const [imageError, setImageError] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);
  
  // Hook for fetching video likes - use fewer avatars for compact display
  const { data: likesData, loading: likesLoading } = useVideoLikes(video.id, 3);

  const handleImageError = () => {
    setImageError(true);
    setImageLoaded(true);
  };

  const handleImageLoad = () => {
    setImageLoaded(true);
  };

  return (
    <Link to={`/videos/${video.id}`} className="group">
      <Card className="overflow-hidden transition-all duration-200 hover:-translate-y-1 hover:shadow-lg">
        {/* Thumbnail */}
        <div className="relative aspect-video bg-muted overflow-hidden">
          {!imageLoaded && (
            <Skeleton className="absolute inset-0" />
          )}
          
          {video.thumbnail_url && !imageError ? (
            <img
              src={video.thumbnail_url}
              alt={video.title}
              className={`w-full h-full object-cover transition-transform duration-200 group-hover:scale-105 ${
                imageLoaded ? 'opacity-100' : 'opacity-0'
              }`}
              onLoad={handleImageLoad}
              onError={handleImageError}
            />
          ) : (
            <div className="w-full h-full bg-linear-to-br from-muted to-muted-foreground/20 flex items-center justify-center">
              <Play className="h-12 w-12 text-muted-foreground" />
            </div>
          )}
          
          {/* Duration overlay */}
          {video.duration && (
            <div className="absolute bottom-2 right-2 bg-black/80 text-white text-xs px-1.5 py-0.5 rounded">
              {formatDuration(video.duration)}
            </div>
          )}
          
          {/* Platform indicator */}
          <div className="absolute top-2 left-2">
            <Badge variant="secondary" className="text-xs capitalize">
              {video.video_platform}
            </Badge>
          </div>
          
          {/* Sequence indicator */}
          {video.sequence && (
            <div className="absolute top-2 right-2">
              <Badge variant="default" className="text-xs">
                Sequence Available
              </Badge>
            </div>
          )}
        </div>

        <CardContent className="p-4">
          {/* Title */}
          <h3 className="font-semibold text-sm line-clamp-2 mb-2 group-hover:text-primary transition-colors">
            {video.title}
          </h3>
          
          {/* Song info */}
          {video.song_name && (
            <p className="text-sm text-muted-foreground line-clamp-1 mb-2">
              {video.artist_name ? `${video.song_name} - ${video.artist_name}` : video.song_name}
            </p>
          )}
          
          {/* Creator */}
          <p className="text-xs text-muted-foreground mb-3">
            by {video.user?.email || 'Unknown User'}
          </p>
          
          {/* Metadata */}
          <div className="flex items-center justify-between text-xs text-muted-foreground">
            <div className="flex items-center gap-3">
              <span className="flex items-center gap-1">
                <Eye className="h-3 w-3" />
                {video.view_count.toLocaleString()}
              </span>
              <span className="flex items-center gap-1">
                <Heart className="h-3 w-3" />
                {video.like_count}
              </span>
            </div>
            <span className="flex items-center gap-1">
              <Calendar className="h-3 w-3" />
              {formatDistanceToNow(new Date(video.created_at), { addSuffix: true })}
            </span>
          </div>
          
          {/* Liked By Section - Compact */}
          {likesData && likesData.totalLikes > 0 && (
            <div className="mt-3">
              <VideoLikedBy
                videoId={video.id}
                likes={likesData.likes}
                totalLikes={likesData.totalLikes}
                loading={likesLoading}
                variant="compact"
                maxAvatars={3}
              />
            </div>
          )}
          
          {/* Display Information */}
          {video.display && (
            <DisplayInfo displayInfo={video.display} />
          )}
          
          {/* Holiday badge */}
          {video.holiday && (
            <div className="mt-3">
              <Badge variant="outline" className="text-xs">
                {video.holiday}
              </Badge>
            </div>
          )}
        </CardContent>
      </Card>
    </Link>
  );
};

const Videos: React.FC = () => {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [videos, setVideos] = useState<Video[]>([]);
  const [loading, setLoading] = useState(true);
  const [totalPages, setTotalPages] = useState(1);
  
  // Initialize state from URL parameters
  const [currentPage, setCurrentPage] = useState(() => {
    return parseInt(searchParams.get('page') || '1');
  });
  
  // Initialize filters from URL parameters
  const [searchQuery, setSearchQuery] = useState(() => {
    return searchParams.get('search') || '';
  });
  const [holidayFilter, setHolidayFilter] = useState<string>(() => {
    return searchParams.get('holiday') || 'all';
  });
  const [yearFilter, setYearFilter] = useState<string>(() => {
    return searchParams.get('display_year') || 'all';
  });
  const [hasSequenceFilter, setHasSequenceFilter] = useState<string>(() => {
    return searchParams.get('has_sequence') || 'all';
  });
  const [sortBy, setSortBy] = useState<string>(() => {
    return searchParams.get('sort_by') || 'recent';
  });

  const fetchVideos = async (params: Partial<VideoListParams> = {}) => {
    setLoading(true);
    try {
      const queryParams = new URLSearchParams();
      
      queryParams.set('page', params.page?.toString() || currentPage.toString());
      queryParams.set('limit', '12');
      
      if (params.search || searchQuery) {
        queryParams.set('search', params.search || searchQuery);
      }
      if ((params.holiday || holidayFilter) && (params.holiday || holidayFilter) !== 'all') {
        queryParams.set('holiday', params.holiday || holidayFilter);
      }
      if ((params.display_year || yearFilter) && (params.display_year?.toString() || yearFilter) !== 'all') {
        queryParams.set('display_year', params.display_year?.toString() || yearFilter);
      }
      if (params.has_sequence !== undefined || (hasSequenceFilter && hasSequenceFilter !== 'all')) {
        queryParams.set('has_sequence', (params.has_sequence || hasSequenceFilter).toString());
      }
      if (params.sort_by || sortBy) {
        queryParams.set('sort_by', params.sort_by || sortBy);
      }

      const response = await fetch(`/api/videos?${queryParams.toString()}`);
      const data: VideoListResponse = await response.json();
      
      if (response.ok) {
        setVideos(data.videos);
        setTotalPages(data.total_pages);
        setCurrentPage(data.page);
      } else {
        console.error('Error fetching videos:', data);
      }
    } catch (error) {
      console.error('Error fetching videos:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Fetch videos with initial parameters from URL
    const initialParams: Partial<VideoListParams> = {
      page: currentPage,
      search: searchQuery || undefined,
      holiday: holidayFilter !== 'all' ? holidayFilter : undefined,
      display_year: yearFilter !== 'all' ? parseInt(yearFilter) : undefined,
      has_sequence: hasSequenceFilter !== 'all' ? (hasSequenceFilter === 'true') : undefined,
      sort_by: sortBy as any
    };
    fetchVideos(initialParams);
  }, []); // Only run once on mount

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    const newPage = 1;
    setCurrentPage(newPage);
    
    // Update URL parameters
    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.set('page', newPage.toString());
    if (searchQuery.trim()) {
      newSearchParams.set('search', searchQuery.trim());
    } else {
      newSearchParams.delete('search');
    }
    setSearchParams(newSearchParams);
    
    fetchVideos({ page: newPage, search: searchQuery });
  };

  const handleFilterChange = (filterType: string, value: string) => {
    const newPage = 1;
    setCurrentPage(newPage);
    const newParams: Partial<VideoListParams> = { page: newPage };
    
    // Update URL parameters
    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.set('page', newPage.toString());
    
    switch (filterType) {
      case 'holiday':
        setHolidayFilter(value);
        newParams.holiday = (value === 'all' || !value) ? undefined : value;
        if (value === 'all' || !value) {
          newSearchParams.delete('holiday');
        } else {
          newSearchParams.set('holiday', value);
        }
        break;
      case 'year':
        setYearFilter(value);
        newParams.display_year = (value === 'all' || !value) ? undefined : parseInt(value);
        if (value === 'all' || !value) {
          newSearchParams.delete('display_year');
        } else {
          newSearchParams.set('display_year', value);
        }
        break;
      case 'hasSequence':
        setHasSequenceFilter(value);
        newParams.has_sequence = value === 'true' ? true : value === 'false' ? false : undefined;
        if (value === 'all') {
          newSearchParams.delete('has_sequence');
        } else {
          newSearchParams.set('has_sequence', value);
        }
        break;
      case 'sort':
        setSortBy(value);
        newParams.sort_by = value as any;
        newSearchParams.set('sort_by', value);
        break;
    }
    
    setSearchParams(newSearchParams);
    fetchVideos(newParams);
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    
    // Update URL with new page
    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.set('page', page.toString());
    setSearchParams(newSearchParams);
    
    fetchVideos({ page });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const clearFilters = () => {
    setSearchQuery('');
    setHolidayFilter('all');
    setYearFilter('all');
    setHasSequenceFilter('all');
    setSortBy('recent');
    setCurrentPage(1);
    
    // Clear all URL parameters except essential ones
    const newSearchParams = new URLSearchParams();
    newSearchParams.set('page', '1');
    newSearchParams.set('sort_by', 'recent');
    setSearchParams(newSearchParams);
    
    fetchVideos({
      page: 1,
      search: undefined,
      holiday: undefined,
      display_year: undefined,
      has_sequence: undefined,
      sort_by: 'recent'
    });
  };

  const generateYearOptions = () => {
    const currentYear = new Date().getFullYear();
    const years = [];
    for (let year = currentYear; year >= 2020; year--) {
      years.push(year);
    }
    return years;
  };

  return (
    <>
      <Helmet>
        <title>Light Display Videos | LightsHunt</title>
        <meta name="description" content="Discover amazing holiday light display videos from creators around the world. Watch Christmas, Halloween, and other seasonal light shows." />
        <meta name="keywords" content="holiday lights, Christmas lights, Halloween lights, light displays, synchronized lights, holiday decorations" />
      </Helmet>

      <div className="min-h-screen flex flex-col bg-background">
        <NavBar />
        
        <main className="flex-1 container mx-auto px-4 py-6 pt-24">
          {/* Header */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-8">
            <div>
              <h1 className="text-3xl font-bold mb-2">Light Display Videos</h1>
              <p className="text-muted-foreground">
                Discover amazing holiday light displays from creators around the world
              </p>
            </div>
            
            {user && (
              <Button asChild className="mt-4 sm:mt-0">
                <Link to="/videos/upload">
                  <Plus className="h-4 w-4 mr-2" />
                  Add Video
                </Link>
              </Button>
            )}
          </div>

          {/* Search and Filters */}
          <div className="mb-8">
            <form onSubmit={handleSearch} className="flex gap-2 mb-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  type="search"
                  placeholder="Search videos, songs, artists..."
                  className="pl-10"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>
              <Button type="submit">Search</Button>
            </form>

            {/* Filter Controls */}
            <div className="flex flex-wrap gap-4 items-center">
              <Select value={holidayFilter} onValueChange={(value) => handleFilterChange('holiday', value)}>
                <SelectTrigger className="w-40">
                  <SelectValue placeholder="All Holidays" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Holidays</SelectItem>
                  {HOLIDAY_OPTIONS.map(option => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select value={yearFilter} onValueChange={(value) => handleFilterChange('year', value)}>
                <SelectTrigger className="w-32">
                  <SelectValue placeholder="All Years" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Years</SelectItem>
                  {generateYearOptions().map(year => (
                    <SelectItem key={year} value={year.toString()}>
                      {year}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select value={hasSequenceFilter} onValueChange={(value) => handleFilterChange('hasSequence', value)}>
                <SelectTrigger className="w-44">
                  <SelectValue placeholder="All Videos" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Videos</SelectItem>
                  <SelectItem value="true">Has Sequence</SelectItem>
                  <SelectItem value="false">No Sequence</SelectItem>
                </SelectContent>
              </Select>

              <Select value={sortBy} onValueChange={(value) => handleFilterChange('sort', value)}>
                <SelectTrigger className="w-36">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {SORT_OPTIONS.map(option => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Button variant="outline" onClick={clearFilters}>
                Clear Filters
              </Button>
            </div>
          </div>

          {/* Videos Grid */}
          {loading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {Array.from({ length: 12 }).map((_, i) => (
                <Card key={i} className="overflow-hidden">
                  <Skeleton className="aspect-video w-full" />
                  <CardContent className="p-4">
                    <Skeleton className="h-4 w-full mb-2" />
                    <Skeleton className="h-3 w-2/3 mb-2" />
                    <Skeleton className="h-3 w-1/2" />
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : videos.length > 0 ? (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 mb-8">
                {videos.map((video) => (
                  <VideoCard key={video.id} video={video} />
                ))}
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex items-center justify-center gap-2">
                  <Button
                    variant="outline"
                    disabled={currentPage === 1}
                    onClick={() => handlePageChange(currentPage - 1)}
                  >
                    Previous
                  </Button>
                  
                  <div className="flex items-center gap-1">
                    {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                      const page = Math.max(1, Math.min(totalPages - 4, currentPage - 2)) + i;
                      return (
                        <Button
                          key={page}
                          variant={currentPage === page ? "default" : "outline-solid"}
                          size="sm"
                          onClick={() => handlePageChange(page)}
                        >
                          {page}
                        </Button>
                      );
                    })}
                  </div>
                  
                  <Button
                    variant="outline"
                    disabled={currentPage === totalPages}
                    onClick={() => handlePageChange(currentPage + 1)}
                  >
                    Next
                  </Button>
                </div>
              )}
            </>
          ) : (
            <div className="text-center py-12">
              <div className="mb-4">
                <Play className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
                <h3 className="text-lg font-semibold mb-2">No videos found</h3>
                <p className="text-muted-foreground">
                  Try adjusting your search terms or filters
                </p>
              </div>
              <Button onClick={clearFilters}>Clear all filters</Button>
            </div>
          )}
        </main>

        <Footer />
      </div>
    </>
  );
};

export default Videos;